import { 
  VersionsResponse, 
  VersionResponse, 
  CreateVersionRequest,
  RevertVersionRequest,
  VersionStats,
  VersionComparison
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// 获取授权头
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// 版本记录API服务
export const versionApi = {
  // 获取提示词的版本列表
  async getVersions(promptId: number, limit = 20, offset = 0): Promise<VersionsResponse> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions?limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`获取版本列表失败: ${response.status}`);
    }

    const result = await response.json();
    
    // 如果API返回了success字段，保持完整格式；否则包装数据
    if (result.success !== undefined) {
      return result; // 返回完整的API响应 {success: true, data: {...}}
    } else {
      return { success: true, data: result }; // 包装旧格式
    }
  },

  // 获取特定版本
  async getVersion(promptId: number, versionNumber: number): Promise<VersionResponse> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions/${versionNumber}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`获取版本失败: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result; // 兼容新的响应格式
  },

  // 比较两个版本
  async compareVersions(promptId: number, version1: number, version2: number): Promise<{ comparison: VersionComparison }> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions/${version1}?compare=${version2}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`版本比较失败: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result; // 兼容新的响应格式
  },

  // 创建新版本记录
  async createVersion(promptId: number, data: CreateVersionRequest): Promise<VersionResponse> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`创建版本记录失败: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result; // 兼容新的响应格式
  },

  // 恢复到指定版本
  async revertToVersion(promptId: number, versionNumber: number, data: RevertVersionRequest): Promise<VersionResponse> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions/${versionNumber}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`版本恢复失败: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result; // 兼容新的响应格式
  },

  // 删除版本
  async deleteVersion(promptId: number, versionNumber: number): Promise<VersionResponse> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions/${versionNumber}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`删除版本失败: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result; // 兼容新的响应格式
  },

  // 获取版本统计信息
  async getVersionStats(promptId: number): Promise<{ stats: VersionStats }> {
    const response = await fetch(
      `${API_BASE_URL}/prompts/${promptId}/versions?limit=1`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`获取版本统计失败: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data || result; // 兼容新的响应格式
    return { stats: data.stats };
  }
};

export default versionApi; 